import React, { useState, useMemo } from 'react';
import { useTable, useSortBy, Column } from 'react-table';
import { getColumnTypeInfo } from '../ApiUtil';
import './TableComponent.css';

// Extend the Column type to include sorting properties
type SortingColumn = Column<object> & {
  isSorted?: boolean;
  isSortedDesc?: boolean;
  getSortByToggleProps?: () => object;
};

// Enhanced column metadata interface
interface ColumnMetadata {
  name: string;
  type: string;
  position: number;
}

type TableProps = {
  columns: SortingColumn[];
  data: any[];
  columnMetadata?: ColumnMetadata[]; // Optional metadata for enhanced formatting
};

// Cell formatter component that formats based on column type
const CellFormatter: React.FC<{ value: any; columnType?: string }> = ({ value, columnType }) => {
  if (!columnType) {
    return <span>{String(value || '')}</span>;
  }

  const typeInfo = getColumnTypeInfo(columnType);
  const formattedValue = typeInfo.formatFunction(value);

  return (
    <span
      style={{ textAlign: typeInfo.alignment }}
      title={`Type: ${columnType} | Raw: ${value}`} // Tooltip showing type and raw value
    >
      {formattedValue}
    </span>
  );
};

// Custom sort functions for different data types
const customSortTypes = {
  // Numeric sorting - handles string numbers properly
  numeric: (rowA: any, rowB: any, columnId: string) => {
    const a = parseFloat(rowA.values[columnId]) || 0;
    const b = parseFloat(rowB.values[columnId]) || 0;
    return a > b ? 1 : a < b ? -1 : 0;
  },

  // Date/time sorting - handles string dates properly
  datetime: (rowA: any, rowB: any, columnId: string) => {
    const aVal = rowA.values[columnId];
    const bVal = rowB.values[columnId];

    // Handle null/undefined values
    if (!aVal && !bVal) return 0;
    if (!aVal) return 1;
    if (!bVal) return -1;

    // Try to parse as dates
    const a = new Date(aVal);
    const b = new Date(bVal);

    // If parsing fails, fall back to string comparison
    if (isNaN(a.getTime()) || isNaN(b.getTime())) {
      return String(aVal).localeCompare(String(bVal));
    }

    return a.getTime() - b.getTime();
  },

  // Boolean sorting - True before False
  boolean: (rowA: any, rowB: any, columnId: string) => {
    const aVal = String(rowA.values[columnId]).toLowerCase();
    const bVal = String(rowB.values[columnId]).toLowerCase();

    const a = aVal === 'true' || aVal === 'yes';
    const b = bVal === 'true' || bVal === 'yes';

    if (a === b) return 0;
    return a ? -1 : 1; // True comes first
  }
};

const TableComponent: React.FC<TableProps> = ({ columns, data, columnMetadata }) => {
  const [searchTerm, setSearchTerm] = useState<string>(''); // State to track the search input

  // Create a lookup map for column types
  const columnTypeMap = useMemo(() => {
    if (!columnMetadata) return {};
    return columnMetadata.reduce((map, col) => {
      map[col.name] = col.type;
      return map;
    }, {} as { [key: string]: string });
  }, [columnMetadata]);

  // Enhanced columns with custom cell rendering and sorting
  const enhancedColumns = useMemo(() => {
    if (!columnMetadata) return columns; // Fallback to original columns if no metadata

    return columns.map((column) => {
      const columnName = column.Header as string;
      const columnType = columnTypeMap[columnName];
      const typeInfo = columnType ? getColumnTypeInfo(columnType) : null;

      // Determine the appropriate sort function based on data type
      let sortFunction;
      if (typeInfo?.jsType === 'number') {
        sortFunction = customSortTypes.numeric;
      } else if (typeInfo?.jsType === 'date') {
        sortFunction = customSortTypes.datetime;
      } else if (typeInfo?.jsType === 'boolean') {
        sortFunction = customSortTypes.boolean;
      }

      const columnConfig = {
        ...column,
        // Custom cell renderer
        Cell: ({ value }: { value: any }) => (
          <CellFormatter value={value} columnType={columnType} />
        ),
        // Custom header styling based on alignment
        headerStyle: typeInfo ? { textAlign: typeInfo.alignment } : undefined
      };

      // Add sort function if we have one
      if (sortFunction) {
        (columnConfig as any).sortType = sortFunction;
      }

      return columnConfig;
    });
  }, [columns, columnMetadata, columnTypeMap]);

  // Use useMemo to prevent recalculating filteredData on every render
  const filteredData = useMemo(() => {
    if (!data) return []; // Return an empty array if `data` is null or undefined

    return data.filter((row) =>
      Object.values(row).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns: enhancedColumns as Column<object>[],
        data: filteredData,
      },
      useSortBy // Enable sorting
    );

  return (
    <div className="table-container">
      {/* Search control */}
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="table-search-input"
      />

      <table {...getTableProps()} className="table-data_table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className="table-data_table_row"
            >
              {headerGroup.headers.map((column) => {
                const sortColumn = column as SortingColumn;
                const sortProps = sortColumn.getSortByToggleProps?.() || {};
                const columnName = column.Header as string;
                const columnType = columnTypeMap[columnName];

                return (
                  <th
                    {...column.getHeaderProps(sortProps)}
                    className="table-data_table_header"
                    style={{
                      cursor: 'pointer'
                    }}
                    title={columnType ? `Column Type: ${columnType}` : undefined}
                  >
                    {column.render('Header')}
                    {sortColumn.isSorted && (
                      <span>
                        {sortColumn.isSortedDesc ? ' 🔽' : ' 🔼'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} className="table-data_table_row">
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()} className="table-data_table_cell">
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TableComponent;